import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FaQuestionCircle, FaLink, FaMicrophone, FaPaperPlane } from "react-icons/fa";
import { FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import { motion } from "framer-motion";

const AccessibleHome = () => {
  const [taskText, setTaskText] = useState("");
  const [messages, setMessages] = useState([]);

  const handleTaskSubmit = () => {
    if (taskText.trim()) {
      const userMessage = { type: "user", text: taskText };
      const sageResponse = {
        type: "sage",
        text: "Got it. Is it urgent or should I remind you later?",
        options: ["It's urgent", "Remind me later"]
      };
      setMessages([...messages.slice(-14), userMessage, sageResponse]);
      setTaskText("");
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#D0D7DB] text-gray-900">
      <header className="w-full bg-[#6C8B74] text-white flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-3">
          <img src="/mnt/data/A_digital_vector_graphic_displays_a_logo_for_Sage.png" alt="Sage Logo" className="h-8 w-auto rounded-sm shadow" />
          <span className="text-xl font-bold">Sage</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Header is now intentionally left empty */}
        </div>
      </header>

      <main className="flex flex-col items-center justify-center px-4 py-12 text-center space-y-6">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold">Hi, I'm Sage.</h1>
        <p className="text-base sm:text-lg md:text-xl max-w-md sm:max-w-lg md:max-w-xl">
          I'm your companion for sorting out life's moving parts — at work, at home, or wherever you're headed. You can use me to keep track of what's on your mind, plan your week, or even offload what's been hanging around in your head.
        </p>

        <div className="w-full max-w-xl space-y-4 mt-6">
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`rounded-lg p-4 shadow-sm text-left border ${msg.type === "sage" ? "bg-[#eef4ef] border-[#bcd1c1]" : "bg-white border-gray-300"}`}
            >
              {msg.type === "user" ? (
                <>
                  <p className="text-gray-800"><strong>You:</strong> {msg.text}</p>
                  <p className="text-sm text-gray-500 mt-1">✓ Sent</p>
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-gray-800"><strong>Sage:</strong> {msg.text}</p>
                  {msg.options && (
                    <div className="flex gap-3 mt-3">
                      {msg.options.map((opt, i) => (
                        <Button key={i} variant="outline" size="sm">{opt}</Button>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          ))}
        </div>

        <div className="w-full max-w-xl pt-4 relative">
          <input
            type="text"
            value={taskText}
            onChange={(e) => setTaskText(e.target.value)}
            placeholder="What's on your mind today? I'll take it from here."
            className="w-full p-4 rounded-md shadow text-base focus:outline-none focus:ring-2 focus:ring-[#3E5544] pr-20"
            aria-label="Start chatting with Sage"
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-2 text-gray-600">
            <button aria-label="Record voice note" className="hover:text-black focus:outline-none">
              <FaMicrophone className="text-xl" />
            </button>
            <button onClick={handleTaskSubmit} aria-label="Submit task" className="hover:text-black focus:outline-none">
              <FaPaperPlane className="text-xl" />
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4 w-full max-w-xs sm:max-w-none">
          <Button className="flex items-center justify-center gap-2 w-full sm:w-auto bg-[#6C8B74] text-white hover:bg-[#57715E] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3E5544]" aria-label="How Sage Helps">
            <FaQuestionCircle /> How Sage Helps
          </Button>
          <Button className="flex items-center justify-center gap-2 w-full sm:w-auto bg-[#6C8B74] text-white hover:bg-[#57715E] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3E5544]" aria-label="Bring Your Calendar">
            <FaLink /> Bring Your Calendar
          </Button>
        </div>
      </main>

      <footer className="mt-auto w-full bg-gray-100 text-center text-sm py-10 px-4 space-y-8">
        <div className="flex flex-wrap justify-center gap-6 text-gray-600">
          <a href="#" className="hover:text-black">About Us</a>
          <a href="#" className="hover:text-black">Contact</a>
          <a href="#" className="hover:text-black">Careers</a>
          <a href="#" className="hover:text-black">Support</a>
          <a href="#" className="hover:text-black">Privacy Policy</a>
          <a href="#" className="hover:text-black">Terms of Service</a>
          <a href="#" className="hover:text-black">How It Works</a>
          <a href="#" className="hover:text-black">Integrations</a>
        </div>

        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6 text-left">
          <Card className="sm:w-1/2">
            <CardContent className="p-4">
              <h2 className="font-semibold mb-2">Why Sage?</h2>
              <p>
                We believe clarity makes space for what matters. Sage is here to support how you plan, prioritize, and carry things forward — without adding friction.
              </p>
            </CardContent>
          </Card>

          <div className="sm:w-1/2 px-4">
            <p className="font-medium mb-2 text-center sm:text-left">Trusted by contributors from:</p>
            <div className="flex flex-wrap justify-center sm:justify-start gap-4">
              <img src="https://www.england.nhs.uk/wp-content/uploads/2018/09/nhs-logo.png" alt="NHS" className="h-6 object-contain" />
              <img src="https://logos.ofsted.gov.uk/images/ofsted-logo.svg" alt="Ofsted" className="h-6 object-contain" />
              <img src="https://upload.wikimedia.org/wikipedia/commons/2/2b/Barnardo%27s_logo.svg" alt="Barnardos" className="h-6 object-contain" />
              <img src="https://upload.wikimedia.org/wikipedia/commons/1/1b/BBC_iPlayer_logo_%282021%29.svg" alt="BBC iPlayer" className="h-6 object-contain" />
              <img src="https://upload.wikimedia.org/wikipedia/commons/4/4e/BBC_Sounds_logo.svg" alt="BBC Podcasts" className="h-6 object-contain" />
              <img src="https://upload.wikimedia.org/wikipedia/commons/6/6e/The_Economist_Logo.svg" alt="The Economist" className="h-6 object-contain" />
            </div>

            <div className="mt-6 flex justify-center sm:justify-start gap-4">
              <a href="#" aria-label="Download on the App Store" className="h-10">
                <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="App Store" className="h-full" />
              </a>
              <a href="#" aria-label="Get it on Google Play" className="h-10">
                <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" className="h-full" />
              </a>
            </div>
          </div>
        </div>

        <div className="flex justify-center space-x-4 text-xl text-gray-700">
          <a href="#" aria-label="Facebook" className="hover:text-black focus:outline-none focus-visible:ring">
            <FaFacebookF />
          </a>
          <a href="#" aria-label="Twitter" className="hover:text-black focus:outline-none focus-visible:ring">
            <FaTwitter />
          </a>
          <a href="#" aria-label="Instagram" className="hover:text-black focus:outline-none focus-visible:ring">
            <FaInstagram />
          </a>
          <a href="#" aria-label="LinkedIn" className="hover:text-black focus:outline-none focus-visible:ring">
            <FaLinkedinIn />
          </a>
        </div>
      </footer>
    </div>
  );
};

export default AccessibleHome;